import { lighten, Theme } from '@mui/material/styles';

export const getMuiButton = (theme: Theme): Record<string, unknown> => {
  const button = {
    padding: {
      small: theme.spacing(1, 1.5),
      medium: theme.spacing(1.5, 3),
      large: theme.spacing(2.5, 4),
    },
  };

  const text = {
    fontSize: {
      small: theme.typography.pxToRem(14),
      medium: theme.typography.pxToRem(16),
      large: theme.typography.pxToRem(18),
    },
  };

  return {
    MuiButton: {
      styleOverrides: {
        root: {
          fontSize: theme.typography.pxToRem(18),
          textTransform: 'none',
          whiteSpace: 'normal',
        },

        label: {
          display: 'flex',
          fontWeight: theme.typography.fontWeightBold,
          letterSpacing: '0.01rem',
          lineHeight: 1.33,
          textTransform: 'none',
        },

        contained: {
          backgroundColor: theme.palette.primary.main,
          border: `1px solid ${theme.palette.primary.main}`,
          color: theme.palette.primary.contrastText,
          padding: button.padding.medium,

          '& .fd-text, & div, & span': {
            color: theme.palette.primary.contrastText,
          },

          '& .MuiButton-startIcon .fd-icon svg, & .MuiButton-endIcon .fd-icon svg':
            {
              color: theme.palette.primary.contrastText,
              lineHeight: text.fontSize.medium,
            },

          '&.Mui-disabled': {
            borderColor: theme.palette.grey[200],
            color: theme.palette.grey[600],

            '& .fd-text, & div, & span': {
              color: theme.palette.grey[600],
            },

            '& .MuiButton-startIcon .fd-icon svg, & .MuiButton-endIcon .fd-icon svg':
              {
                color: theme.palette.grey[600],
              },
          },

          '&:hover': {
            borderColor: theme.palette.primary.light,
            backgroundColor: theme.palette.primary.light,

            '@media (hover: none)': {
              borderColor: theme.palette.primary.main,
              backgroundColor: theme.palette.primary.main,
            },
          },
        },

        outlined: {
          backgroundColor: 'transparent',
          borderColor: theme.palette.primary.main,
          color: theme.palette.primary.main,
          padding: button.padding.medium,

          '& .fd-text, & div, & span': {
            color: theme.palette.primary.main,
          },

          '& .MuiButton-startIcon .fd-icon svg, & .MuiButton-endIcon .fd-icon svg':
            {
              color: theme.palette.primary.main,
              lineHeight: text.fontSize.medium,
            },

          '&.Mui-disabled': {
            '& .fd-text, & div, & span': {
              color: theme.palette.grey[600],
            },

            '& .MuiButton-startIcon .fd-icon svg, & .MuiButton-endIcon .fd-icon svg':
              {
                color: theme.palette.grey[600],
              },
          },

          '&:hover': {
            backgroundColor: lighten(theme.palette.primary.main, 0.8),

            '@media (hover: none)': {
              backgroundColor: 'transparent',
            },
          },
        },

        text: {
          borderBottom: `1px dashed ${theme.palette.primary.main}`,
          color: theme.palette.common.black,
          padding: 0,
          transition: '0.2s',
          fontSize: theme.typography.fontSize,
          fontWeight: theme.typography.fontWeightRegular,

          '& span': {
            fontFamily: theme.typography.fontFamily,
          },

          '& .MuiButton-startIcon .fd-icon svg, & .MuiButton-endIcon .fd-icon svg':
            {
              color: theme.palette.primary.main,
              lineHeight: text.fontSize.medium,
            },

          '&.Mui-disabled': {
            '& .MuiButton-label': {
              color: theme.palette.grey[600],
            },

            '& .MuiButton-startIcon .fd-icon svg, & .MuiButton-endIcon .fd-icon svg':
              {
                color: theme.palette.grey[600],
              },
          },

          '&:hover': {
            backgroundColor: 'transparent',
            borderBottom: `1px solid ${theme.palette.primary.main}`,
          },

          '&.noUnderline': {
            border: 'none',
          },
        },

        containedSizeSmall: {
          fontSize: text.fontSize.small,
          padding: button.padding.small,
        },

        containedSizeLarge: {
          fontSize: text.fontSize.large,
          padding: button.padding.large,
        },

        outlinedSizeSmall: {
          fontSize: text.fontSize.small,
          padding: button.padding.small,
        },

        outlinedSizeLarge: {
          fontSize: text.fontSize.large,
          padding: button.padding.large,
        },

        textSizeSmall: {
          padding: theme.spacing(0),

          '& .MuiButton-label': {
            fontSize: text.fontSize.small,
          },
        },

        textSizeLarge: {
          padding: theme.spacing(0),

          '& .MuiButton-label': {
            fontSize: text.fontSize.large,
          },
        },
      },
    },
  };
};
